import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import RequiredMark, { withRequiredMark } from '@/temu-platform/components/RequiredMark';
import PublishImageUploadZone from '@/temu-platform/modules/products/components/PublishImageUploadZone';
import CustomAttributeValueModal from '@/temu-platform/modules/products/components/CustomAttributeValueModal';
import { appendPrintNamesAfterHyphen } from '@/pages/ipe/workbench/utils/platformListingItemMedia';
import {
  attributeDisplayLabel,
  attributeValueDisplayLabel,
} from '@/temu-platform/utils/temuAttributeTemplate';
import {
  resolveTemuPrintRiskMeta,
  temuPrintRiskBadgeClass,
} from '@/temu-platform/utils/temuPublishPrintInfringement';
import TemuUploadSourceMenu from '@/temu-platform/components/TemuUploadSourceMenu';
import FieldHelpMark from '@/components/FieldHelpMark';
import LibraryModalContent from '@/pages/ipe/workbench/modals/LibraryModalContent';
import VideoSpacePreviewModal from '@/pages/ipe/workbench/components/VideoSpacePreviewModal';
import { appendUniqueImageSpaceRows } from '@/pages/ipe/workbench/utils/imageSpacePageFetch';
import {
  TEMU_PUBLISH_VIDEO_ACCEPT,
  TEMU_PUBLISH_VIDEO_RATIO_HINT,
  assertTemuPublishVideoRatio,
  videoSpaceRecordToUploadFile,
} from '@/temu-platform/utils/temuVideoSpacePick';
import { resolveVideoSpacePlayUrl } from '@/pages/ipe/workbench/utils/downloadVideoSpaceVideo';
import {
  TEMU_CLOTHING_SKC_CAROUSEL_MIN,
  TEMU_MAX_DETAIL_IMAGES,
  TEMU_MATERIAL_IMAGE_HINT,
  TEMU_PRODUCT_IMAGE_HINT,
} from '@/temu-platform/utils/temuProductImage';
import {
  deriveTemuColorBlockFromMainSlot,
} from '@/temu-platform/utils/temuColorBlock';
import { deriveTemuMaterialFromMainSlot } from '@/temu-platform/utils/temuMaterialFromMain';
import { temuPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

const MAX_PRODUCT_VIDEO_BYTES = 20 * 1024 * 1024;
const TEMU_MATERIAL_HINT = `素材图要求：${TEMU_MATERIAL_IMAGE_HINT}。仅可上传 1 张；上传第一个颜色的主图后会自动裁成 1:1 素材图，也可手动上传正方形素材图。`;
const TEMU_MAIN_VIDEO_HINT = TEMU_PUBLISH_VIDEO_RATIO_HINT;
const TEMU_SKC_PRODUCT_IMAGE_HINT = `商品图（主图+细节图，服饰至少 ${TEMU_CLOTHING_SKC_CAROUSEL_MIN} 张）：${TEMU_PRODUCT_IMAGE_HINT}。色块图由系统按主图自动裁切；素材图仅取第一个颜色的主图自动裁 1:1。`;
/** 细节图最多张数（主图单独 1 张；合计最多 10，对齐平台服饰轮播上限） */
const MAX_DETAIL_IMAGES = TEMU_MAX_DETAIL_IMAGES;

/** 印花图最多张数（站内字段，仅展示 / 侵权检测，不自动拼 SKU，不提交 TEMU） */
const MAX_PRINT_IMAGES = 10;

const BATCH_FIELD_META = {
  price: { title: '批量填写价格', placeholder: '如 9.99', inputMode: 'decimal' },
  suggestedPrice: { title: '批量填写建议零售价', placeholder: '如 19.99', inputMode: 'decimal' },
  stock: { title: '批量填写库存', placeholder: '如 100', inputMode: 'numeric' },
  supplierSku: { get title() { return fixedUi("批量填写商家 SKU"); }, placeholder: '如 SPU-颜色编码-尺码', inputMode: 'text' },
};

const BATCH_TITLE_KEY = {
  price: 'publishSpec.batchPrice',
  suggestedPrice: 'publishSpec.batchMsrp',
  stock: 'publishSpec.batchStock',
  supplierSku: 'publishSpec.batchMerchantSku',
};
const BATCH_PH_KEY = {
  price: 'publishSpec.phPrice',
  suggestedPrice: 'publishSpec.phMsrp',
  stock: 'publishSpec.phStock',
  supplierSku: 'publishSpec.phSkuPattern',
};

function sanitizeSkuPart(value) {
  return String(value ?? '').trim();
}

/** 从规格印花图槽位取出可拼接的名称（多张用 +；无名则空） */
export function patternNameFromPrintImages(images) {
  return (Array.isArray(images) ? images : [])
    .map((img) => sanitizeSkuPart(img?.name))
    .filter(Boolean)
    .join('+');
}

/** 弹窗未填印花名时，用规格印花图名称；都没有则为空 */
export function resolveSupplierSkuPatternName(input, printImages) {
  return sanitizeSkuPart(input) || patternNameFromPrintImages(printImages);
}

/** 用短横线拼接；未勾选/无图案时不加末尾「-」 */
function joinSkuPartsWithOptionalPattern(parts, patternName, { maxLen, appendPattern }) {
  const base = (Array.isArray(parts) ? parts : []).map(sanitizeSkuPart).filter(Boolean);
  if (!base.length) return '';
  const pattern = appendPattern ? sanitizeSkuPart(patternName) : '';
  const joined = pattern ? `${base.join('-')}-${pattern}` : base.join('-');
  return joined.slice(0, maxLen);
}

/** 商家 SKU：SPU-颜色编码-尺码；勾选并填写图案时为 …-尺码-图案编号 */
export function buildMerchantSupplierSku({
  spu,
  colorCode,
  sizeLabel,
  patternName,
  includePattern = false,
}) {
  return joinSkuPartsWithOptionalPattern(
    [spu, colorCode, sizeLabel],
    patternName,
    { maxLen: 200, appendPattern: Boolean(includePattern) },
  );
}

/** 货号：SPU-颜色编码；填写图案编号时为 SPU-颜色编码-图案编号（联动批量弹窗的款号/图案） */
export function buildSkcSupplierCode({ spu, colorCode, patternName = '' }) {
  return joinSkuPartsWithOptionalPattern(
    [spu, colorCode],
    patternName,
    { maxLen: 100, appendPattern: Boolean(sanitizeSkuPart(patternName)) },
  );
}

/** 去掉末尾预留 - 后按短横线分段 */
export function splitSkuCodeSegments(value) {
  const raw = sanitizeSkuPart(value).replace(/-+$/, '');
  if (!raw) return [];
  return raw.split('-').map((part) => part.trim()).filter(Boolean);
}

/**
 * 用已保存的颜色编码，从「SPU-颜色编码-…」反推 SPU（允许 SPU 自身含 -）。
 * 禁止把整段货号当 SPU。
 */
export function extractSpuBeforeColorCode(codeValue, colorCode) {
  const raw = sanitizeSkuPart(codeValue).replace(/-+$/, '');
  const color = sanitizeSkuPart(colorCode);
  if (!raw || !color) return '';
  const marker = `-${color}`;
  // 从右匹配，避免 SPU 自身含相同片段时误切
  let idx = raw.lastIndexOf(marker);
  while (idx > 0) {
    const after = raw.slice(idx + marker.length);
    if (!after || after.startsWith('-')) {
      return raw.slice(0, idx);
    }
    idx = raw.lastIndexOf(marker, idx - 1);
  }
  return '';
}

/**
 * 从货号 / 商家 SKU 提取 SPU。
 * 优先：已存 colorCode + 货号/SKU 反推；否则按标准段位取第一段（不能把整段货号当 SPU）。
 */
export function inferSpuFromSkcList(skcList = []) {
  for (const skc of (Array.isArray(skcList) ? skcList : [])) {
    const colorCode = sanitizeSkuPart(skc?.colorCode);
    if (colorCode) {
      const fromCode = extractSpuBeforeColorCode(skc?.supplierCode, colorCode);
      if (fromCode) return fromCode;
      const firstSku = (skc?.skuRows || []).find((row) => sanitizeSkuPart(row?.supplierSku));
      const fromSku = extractSpuBeforeColorCode(firstSku?.supplierSku, colorCode);
      if (fromSku) return fromSku;
    }
  }
  // 无 colorCode 时：货号至少 SPU-颜色，商家 SKU 至少 SPU-颜色-尺码 → 取第一段
  for (const skc of (Array.isArray(skcList) ? skcList : [])) {
    const codeParts = splitSkuCodeSegments(skc?.supplierCode);
    if (codeParts.length >= 2) return codeParts[0];
    const firstSku = (skc?.skuRows || []).find((row) => sanitizeSkuPart(row?.supplierSku));
    const skuParts = splitSkuCodeSegments(firstSku?.supplierSku);
    if (skuParts.length >= 3) return skuParts[0];
    if (skuParts.length >= 1) return skuParts[0];
  }
  return '';
}

/**
 * 颜色编码优先用 skc.colorCode；否则从货号第二段或商家 SKU 第二段解析。
 * 货号：SPU-颜色[-图案]；商家 SKU：SPU-颜色-尺码[-图案]
 * （无 colorCode 且 SPU 含 - 时，第二段可能不准，故批量写入会持久化 colorCode）
 */
export function inferColorCodeFromSkc(skc) {
  const saved = sanitizeSkuPart(skc?.colorCode);
  if (saved) return saved;

  const codeParts = splitSkuCodeSegments(skc?.supplierCode);
  if (codeParts.length >= 2) return codeParts[1];

  const firstSku = (skc?.skuRows || []).find((row) => sanitizeSkuPart(row?.supplierSku));
  if (firstSku) {
    const skuParts = splitSkuCodeSegments(firstSku.supplierSku);
    if (skuParts.length >= 2) return skuParts[1];
  }
  return '';
}

/** 兼容旧调用：用 SPU 前缀匹配；若前缀实为完整货号则回退到第二段色码 */
export function inferColorCodeFromSupplierSku(supplierSku, spuPrefix) {
  const raw = sanitizeSkuPart(supplierSku).replace(/-+$/, '');
  const spu = sanitizeSkuPart(spuPrefix).replace(/-+$/, '');
  if (!raw) return '';
  if (spu && raw.startsWith(`${spu}-`)) {
    const rest = raw.slice(spu.length + 1);
    const next = rest.split('-').filter(Boolean)[0] || '';
    // 前缀过长（整段货号）时 rest 往往直接是尺码，交给第二段兜底
    const parts = splitSkuCodeSegments(raw);
    if (parts.length >= 3 && next && parts[parts.length - 1] === next && parts[1] !== next) {
      return parts[1] || '';
    }
    return next;
  }
  const parts = splitSkuCodeSegments(raw);
  return parts.length >= 2 ? (parts[1] || '') : '';
}

export function inferColorCodesFromSkcList(skcList) {
  const codes = {};
  (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
    if (!skcHasColor(skc)) return;
    const key = skcColorKey(skc);
    if (!key || codes[key]) return;
    const inferred = inferColorCodeFromSkc(skc);
    if (inferred) codes[key] = inferred;
  });
  return codes;
}

/**
 * 从货号/商家 SKU 回填图案编号。
 * 有 colorCode 时：货号去掉「SPU-颜色」后的剩余；商家 SKU 去掉「SPU-颜色-尺码」后的剩余。
 */
export function inferPatternNameFromSkcList(skcList = []) {
  for (const skc of (Array.isArray(skcList) ? skcList : [])) {
    const colorCode = sanitizeSkuPart(skc?.colorCode);
    const supplierCode = sanitizeSkuPart(skc?.supplierCode).replace(/-+$/, '');
    if (colorCode && supplierCode) {
      const spu = extractSpuBeforeColorCode(supplierCode, colorCode);
      if (spu) {
        const prefix = `${spu}-${colorCode}`;
        if (supplierCode === prefix) return '';
        if (supplierCode.startsWith(`${prefix}-`)) {
          return supplierCode.slice(prefix.length + 1);
        }
      }
    }
    const firstSku = (skc?.skuRows || []).find((row) => sanitizeSkuPart(row?.supplierSku));
    const supplierSku = sanitizeSkuPart(firstSku?.supplierSku).replace(/-+$/, '');
    if (colorCode && supplierSku) {
      const spu = extractSpuBeforeColorCode(supplierSku, colorCode);
      if (spu) {
        const afterColor = supplierSku.slice(`${spu}-${colorCode}`.length);
        // afterColor = -尺码 或 -尺码-图案
        const restParts = afterColor.replace(/^-/, '').split('-').filter(Boolean);
        if (restParts.length >= 2) return restParts.slice(1).join('-');
      }
    }
  }
  return '';
}

export function patternNamesFromPatternText(patternName) {
  return sanitizeSkuPart(patternName)
    .split('+')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function appendPatternNameToSupplierSku(supplierSku, patternName) {
  const names = patternNamesFromPatternText(patternName);
  if (!names.length) return supplierSku;
  return appendPrintNamesAfterHyphen(supplierSku, names, { maxLen: 200 });
}

function resolveSkuSizeLabel(sku, sizeOptions = []) {
  if (sku?.sizeIsCustom || sku?.sizeExtraValue) {
    return attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '';
  }
  return attributeValueDisplayLabel(
    sizeOptions.find((v) => String(v.id) === String(sku?.sizeValueId)) || sku?.sizeLabel,
  ) || '';
}

function PrintInfringementResultModal({ open, riskLabel, assessedAt, body, onClose }) {
  if (!open) return null;

  const formattedAt = assessedAt
    ? (() => {
      try {
        return new Date(assessedAt).toLocaleString();
      } catch {
        return assessedAt;
      }
    })()
    : '';

  return (
    <div
      className="tpo-batch-modal"
      role="dialog"
      aria-modal="true"
      aria-label={specText('publishSpec.riskTitle', fixedUi("侵权检测结果"))}
    >
      <div className="tpo-batch-modal__panel tpo-batch-modal__panel--infringement">
        <div className="tpo-batch-modal__head">
          <strong>{specText('publishSpec.riskTitle', fixedUi("侵权检测结果"))}</strong>
          <button type="button" className="tpo-batch-modal__close" aria-label={specText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="tpo-batch-modal__body tpo-infr-result-modal__body">
          <div className="tpo-infr-result-modal__meta">
            <div><strong>{specText('publishSpec.riskStatus', fixedUi("检测状态："))}</strong>{riskLabel || '—'}</div>
            {formattedAt ? <div><strong>{specText('publishSpec.riskTime', fixedUi("检测时间："))}</strong>{formattedAt}</div> : null}
          </div>
          <div className="tpo-infr-result-modal__detail">{body || specText('publishSpec.riskEmpty', fixedUi("暂无详细检测说明"))}</div>
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onClose}>{specText('moduleShell.close', fixedUi("关闭"))}</button>
        </div>
      </div>
    </div>
  );
}

function BatchFillModal({ field, currency, onClose, onConfirm, titleOverride }) {
  const meta = BATCH_FIELD_META[field] || BATCH_FIELD_META.price;
  const title = titleOverride || specText(BATCH_TITLE_KEY[field] || 'publishSpec.batchPrice', meta.title);
  const placeholder = specText(BATCH_PH_KEY[field] || 'publishSpec.fill', meta.placeholder);
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="tpo-batch-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="tpo-batch-modal__panel">
        <div className="tpo-batch-modal__head">
          <strong>{title}{(field === 'price' || field === 'suggestedPrice') && currency ? ` (${currency})` : ''}</strong>
          <button type="button" className="tpo-batch-modal__close" aria-label={specText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="tpo-batch-modal__body">
          <p className="tpo-batch-modal__hint">{specText('publishSpec.applyAllRows', fixedUi("将应用到当前所有 SKU 行"))}</p>
          <input
            ref={inputRef}
            className="tpo-input"
            value={value}
            inputMode={meta.inputMode}
            placeholder={placeholder}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onConfirm?.(value);
              }
            }}
          />
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>{specText('shop.cancel', fixedUi("取消"))}</button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary tpo-btn--sm"
            onClick={() => onConfirm?.(value)}
          >
            {specText('moduleShell.confirmOk', '确定')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 货号 / 商家 SKU 共用批量：SPU + 颜色编码 + 图案编号；两边同步写入 */
function SupplierCodeBatchModal({
  colors = [],
  defaultSpu = '',
  skcList = [],
  title = '批量填写货号 / 商家 SKU',
  onClose,
  onConfirm,
}) {
  const initialColorCodes = useMemo(() => inferColorCodesFromSkcList(skcList), [skcList]);
  const resolvedDefaultSpu = useMemo(() => {
    const fromList = inferSpuFromSkcList(skcList);
    if (fromList) return fromList;
    const raw = sanitizeSkuPart(defaultSpu).replace(/-+$/, '');
    if (!raw) return '';
    // defaultSpu 常被误传成完整货号：用任一已推断色码反推 SPU
    const sampleColor = Object.values(initialColorCodes).find(Boolean);
    if (sampleColor) {
      const extracted = extractSpuBeforeColorCode(raw, sampleColor);
      if (extracted) return extracted;
    }
    // 仍像「SPU-颜色」货号时，不要整段塞进 SPU
    const parts = splitSkuCodeSegments(raw);
    if (parts.length >= 2) return parts[0];
    return raw;
  }, [skcList, defaultSpu, initialColorCodes]);

  const initialSpuRef = useRef(resolvedDefaultSpu);
  const initialColorCodesRef = useRef(initialColorCodes);
  const initialPatternRef = useRef(inferPatternNameFromSkcList(skcList));
  const [spu, setSpu] = useState(() => initialSpuRef.current);
  const [patternName, setPatternName] = useState(() => initialPatternRef.current);
  const [includePatternInSku, setIncludePatternInSku] = useState(
    () => Boolean(initialPatternRef.current),
  );
  const [colorCodes, setColorCodes] = useState(() => {
    const inferred = initialColorCodesRef.current;
    const init = {};
    colors.forEach((c) => { init[c.key] = inferred[c.key] || ''; });
    return init;
  });
  const [error, setError] = useState('');
  const spuRef = useRef(null);

  useEffect(() => {
    spuRef.current?.focus();
  }, []);

  const handleConfirm = () => {
    const nextSpu = sanitizeSkuPart(spu);
    const nextPattern = sanitizeSkuPart(patternName);
    const inferred = inferColorCodesFromSkcList(skcList);
    const mergedCodes = {};
    colors.forEach((c) => {
      mergedCodes[c.key] = sanitizeSkuPart(colorCodes[c.key]) || inferred[c.key] || '';
    });

    if (!nextSpu) {
      setError(specText('publishSpec.needSpu', '请填写 SPU'));
      return;
    }
    if (!colors.length) {
      setError(specText('publishSpec.pickMainColors', '请先选择主规格颜色'));
      return;
    }
    const missing = colors.filter((c) => !mergedCodes[c.key]);
    if (missing.length) {
      setError(specText('publishSpec.needColorCodes', `请填写颜色编码：${missing.map((c) => c.label).slice(0, 3).join('、')}`, { names: missing.map((c) => c.label).slice(0, 3).join('、') }));
      return;
    }
    if (includePatternInSku && !nextPattern) {
      setError(specText('publishSpec.needPattern', '已勾选在商家 SKU 添加图案编号，请填写图案编号'));
      return;
    }
    onConfirm?.({
      spu: nextSpu,
      patternName: nextPattern,
      colorCodes: mergedCodes,
      includePatternInSku: Boolean(includePatternInSku),
    });
  };

  return (
    <div className="tpo-batch-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="tpo-batch-modal__panel tpo-batch-modal__panel--supplier-sku">
        <div className="tpo-batch-modal__head">
          <strong>{title}</strong>
          <button type="button" className="tpo-batch-modal__close" aria-label={specText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="tpo-batch-modal__body">
          <div className="tpo-field">
            <span className="tpo-field__label">SPU <RequiredMark /></span>
            <input
              ref={spuRef}
              className="tpo-input"
              value={spu}
              placeholder={specText('publishSpec.phSpu', '如 1001')}
              onChange={(e) => setSpu(e.target.value)}
            />
          </div>
          <div className="tpo-supplier-sku-colors">
            <span className="tpo-field__label">{specText('publishSpec.colorCode', fixedUi("颜色编码"))} <RequiredMark /></span>
            {colors.length ? colors.map((c) => (
              <div key={c.key} className="tpo-supplier-sku-color-row">
                <span className="tpo-supplier-sku-color-row__name" title={c.label}>{c.label}</span>
                <input
                  className="tpo-input"
                  value={colorCodes[c.key] || ''}
                  placeholder={specText('publishSpec.phWh', '如 WH')}
                  onChange={(e) => setColorCodes((prev) => ({ ...prev, [c.key]: e.target.value }))}
                />
              </div>
            )) : (
              <p className="tpo-batch-modal__hint">{specText('publishSpec.noColors', fixedUi("暂无已选颜色"))}</p>
            )}
          </div>
          <div className="tpo-field">
            <span className="tpo-field__label">{specText('publishSpec.patternNo', fixedUi("图案编号"))}</span>
            <input
              className="tpo-input"
              value={patternName}
              placeholder={specText('publishSpec.patternOptional', fixedUi("可选"))}
              onChange={(e) => setPatternName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleConfirm();
                }
              }}
            />
          </div>
          <label className="tpo-supplier-sku-pattern-opt">
            <input
              type="checkbox"
              checked={includePatternInSku}
              onChange={(e) => setIncludePatternInSku(e.target.checked)}
            />
            <span>{specText('publishSpec.addPatternToSku', fixedUi("在商家 SKU 添加图案编号"))}</span>
          </label>
          {error ? <p className="tpo-field__error" role="alert">{error}</p> : null}
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>{specText('shop.cancel', fixedUi("取消"))}</button>
          <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={handleConfirm}>
            {specText('moduleShell.confirmOk', '确定')}
          </button>
        </div>
      </div>
    </div>
  );
}

function BatchHeader({ children, onBatch }) {
  const { t } = useTranslation('workbench');
  return (
    <th>
      <span className="tpo-supply-th">
        <span>{children}</span>
        <button type="button" className="tpo-supply-th__batch" onClick={onBatch}>
          {t('publishSpec.batch')}
        </button>
      </span>
    </th>
  );
}

// 槽位文案为站内自定义，非平台接口字段名（展示前去掉「(中文)」等脏标记）
function optionName(v) {
  return attributeValueDisplayLabel(v) || String(v?.id || '');
}

function isCustomSpecKey(key) {
  return String(key || '').startsWith('custom:');
}

function customLabelFromKey(key) {
  try {
    return decodeURIComponent(String(key).slice('custom:'.length));
  } catch {
    return String(key).slice('custom:'.length);
  }
}

export function makeCustomSpecKey(label) {
  return `custom:${encodeURIComponent(String(label || '').trim())}`;
}

/** SKC 颜色在选择器中的稳定 key（平台 valueId 或 custom:xxx） */
export function skcColorKey(skc) {
  if (!skc) return '';
  if (skc.colorIsCustom || skc.colorExtraValue) {
    return makeCustomSpecKey(skc.colorExtraValue || skc.colorLabel || '');
  }
  return String(skc.colorValueId || '');
}

export function skcHasColor(skc) {
  return Boolean(skc && (skc.colorValueId || String(skc.colorExtraValue || '').trim() || String(skc.colorLabel || '').trim()));
}

/**
 * 规格值选择。
 * - 默认：多选勾选（尺码）
 * - allowCustom + addMode：点选即添加（可连续选）；回车可添加自定义颜色
 *   （对齐商家后台「请选择或自定义」；OpenAPI 主规格自定义须用 custom_attribute_value，禁止 attribute_value_id=0）
 */
export function SpecValuePicker({
  options = [],
  value = [],
  onChange,
  placeholder = '请选择或搜索（可多选）',
  disabled = false,
  emptyText = fixedUi('暂无平台选项'),
  disabledHint = '暂不可选',
  allowCustom = false,
  addMode = false,
  /** key → 展示名；属性模板未就绪时用平台回填的 attributeValueName，禁止直接显示 valueId */
  labelByKey = {},
  /** 平台 has_permission=1：展示「申请新增」并走 add-custom-attribute-value */
  allowPlatformCustom = false,
  shopId = '',
  categoryId = '',
  attributeId = '',
  attributeLabel = '',
  onPlatformCustomAdded,
}) {
  const { t } = useTranslation('workbench');
  const rootRef = useRef(null);
  const controlRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [panelStyle, setPanelStyle] = useState({});
  const [customOpen, setCustomOpen] = useState(false);
  const [extraOptions, setExtraOptions] = useState([]);

  useEffect(() => {
    setExtraOptions([]);
    setCustomOpen(false);
  }, [attributeId, categoryId]);

  const normalizedOptions = useMemo(() => {
    const base = (options || []).map((opt) => ({ ...opt, id: String(opt.id) }));
    const seen = new Set(base.map((opt) => opt.id));
    const extras = extraOptions.filter((opt) => opt?.id && !seen.has(String(opt.id)));
    return extras.length
      ? [...base, ...extras.map((opt) => ({ ...opt, id: String(opt.id) }))]
      : base;
  }, [options, extraOptions]);

  const selectedKeys = useMemo(
    () => (Array.isArray(value) ? value.map(String).filter(Boolean) : []),
    [value],
  );

  const selectedTags = useMemo(() => selectedKeys.map((key) => {
    if (isCustomSpecKey(key)) {
      return {
        key,
        label: attributeValueDisplayLabel(customLabelFromKey(key)) || customLabelFromKey(key),
        custom: true,
      };
    }
    const opt = normalizedOptions.find((item) => item.id === key);
    const fallback = labelByKey[key];
    return {
      key,
      label: opt
        ? optionName(opt)
        : (attributeValueDisplayLabel(fallback) || fallback || key),
      custom: false,
    };
  }), [selectedKeys, normalizedOptions, labelByKey]);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter((opt) => optionName(opt).toLowerCase().includes(q));
  }, [normalizedOptions, keyword]);

  const updatePanelPosition = () => {
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const maxHeight = Math.min(280, Math.max(140, window.innerHeight - rect.bottom - 12));
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 280),
      maxHeight,
      zIndex: 14000,
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, filtered.length, selectedKeys.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inRoot && !inPanel) {
        setOpen(false);
        setKeyword('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const addKey = (key) => {
    const nextKey = String(key || '').trim();
    if (!nextKey) return;
    if (selectedKeys.includes(nextKey)) return;
    onChange([...selectedKeys, nextKey]);
  };

  const toggle = (id) => {
    const nextId = String(id);
    if (addMode) {
      addKey(nextId);
      return;
    }
    const set = new Set(selectedKeys);
    if (set.has(nextId)) set.delete(nextId);
    else set.add(nextId);
    onChange([...set]);
  };

  const remove = (key) => onChange(selectedKeys.filter((item) => item !== String(key)));

  const addCustomFromKeyword = () => {
    const text = keyword.trim();
    if (!text) return false;
    const matched = normalizedOptions.find((opt) => optionName(opt).toLowerCase() === text.toLowerCase());
    if (matched) {
      addKey(matched.id);
      setKeyword('');
      return true;
    }
    if (allowCustom) {
      addKey(makeCustomSpecKey(text));
      setKeyword('');
      return true;
    }
    return false;
  };

  const canOpen = !disabled && (normalizedOptions.length > 0 || allowCustom);
  const inputPlaceholder = disabled
    ? disabledHint
    : (normalizedOptions.length === 0 && !allowCustom
      ? emptyText
      : placeholder);
  const canPlatformCustom = Boolean(
    allowPlatformCustom && shopId && categoryId && attributeId && !disabled,
  );

  const onPlatformAdded = (opt) => {
    if (!opt?.id) return;
    setExtraOptions((prev) => (
      prev.some((item) => String(item.id) === String(opt.id))
        ? prev
        : [...prev, { id: String(opt.id), name: opt.name, nameCn: opt.nameCn || opt.name }]
    ));
    onPlatformCustomAdded?.(opt);
    addKey(String(opt.id));
  };

  const panel = open && canOpen ? createPortal(
    <div
      ref={panelRef}
      className="tpo-spec-picker__panel tpo-spec-picker__panel--portal"
      style={panelStyle}
    >
      {filtered.length === 0 ? (
        <div className="tpo-spec-picker__empty">
          {allowCustom && keyword.trim()
            ? t('publishSpec.enterCustom', { name: keyword.trim() })
            : t('productCopy.emptyOptions')}
        </div>
      ) : (
        <div className="tpo-spec-picker__list">
          {filtered.map((opt) => {
            const checked = selectedKeys.includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                className={`tpo-spec-picker__option${checked ? ' is-on' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  toggle(opt.id);
                  if (addMode) setKeyword('');
                }}
              >
                {!addMode ? (
                  <input type="checkbox" readOnly checked={checked} tabIndex={-1} />
                ) : null}
                <span>{optionName(opt)}</span>
                {addMode && checked ? <em className="tpo-spec-picker__added">{t('publishSpec.added')}</em> : null}
              </button>
            );
          })}
        </div>
      )}
      <div className="tpo-spec-picker__footer">
        {selectedKeys.length > 0 ? (
          <button
            type="button"
            className="tpo-btn tpo-btn--ghost tpo-btn--sm"
            onClick={() => onChange([])}
          >
            {t('publishAttr.clear')}
          </button>
        ) : <span />}
        <button
          type="button"
          className="tpo-btn tpo-btn--secondary tpo-btn--sm"
          onClick={() => {
            setOpen(false);
            setKeyword('');
          }}
        >
          {t('returnList.completed')}
        </button>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div
      className={`tpo-spec-picker${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
      ref={rootRef}
    >
      <div className="tpo-spec-picker__row">
        {selectedTags.length > 0 ? (
          <div className="tpo-spec-picker__tags">
            {selectedTags.map((tag) => (
              <span key={tag.key} className={`tpo-spec-picker__tag${tag.custom ? ' is-custom' : ''}`}>
                <em>{tag.label}</em>
                <button
                  type="button"
                  aria-label={t('productCopy.remove')}
                  disabled={disabled}
                  onClick={() => remove(tag.key)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <div
          className="tpo-spec-picker__control"
          ref={controlRef}
          onClick={() => {
            if (canOpen) setOpen(true);
          }}
        >
          <input
            className="tpo-spec-picker__search"
            value={keyword}
            disabled={disabled || (!allowCustom && normalizedOptions.length === 0)}
            placeholder={inputPlaceholder}
            onFocus={() => {
              if (canOpen) setOpen(true);
            }}
            onChange={(e) => {
              setKeyword(e.target.value);
              if (canOpen) setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (addMode || allowCustom) {
                  if (!addCustomFromKeyword() && filtered[0]) addKey(filtered[0].id);
                } else if (filtered[0]) {
                  toggle(filtered[0].id);
                }
                setKeyword('');
              }
              if (e.key === 'Escape') {
                setOpen(false);
                setKeyword('');
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <span className="tpo-spec-picker__caret" aria-hidden>▾</span>
        </div>
      </div>
      {panel}
      {canPlatformCustom ? (
        <div className="tpo-attr-custom-hint">
          <span>{t('publishAttr.noValue')}</span>
          <button
            type="button"
            className="tpo-attr-custom-hint__link"
            onClick={() => setCustomOpen(true)}
          >
            {t('publishAttr.applyNew')}
          </button>
        </div>
      ) : null}
      <CustomAttributeValueModal
        open={customOpen}
        shopId={shopId}
        categoryId={categoryId}
        attributeId={attributeId}
        attributeLabel={attributeLabel}
        onClose={() => setCustomOpen(false)}
        onAdded={onPlatformAdded}
      />
    </div>
  );
}

/**
 * 规格供应：主规格 / 其他规格（名称来自类目销售属性模板，不写死「颜色/尺码」）
 * → 自动生成主规格图行 + 供应信息表
 */
function specText(key, fallback, options) {
  if (!i18n.isInitialized) return fallback;
  return i18n.t(key, { ns: 'workbench', defaultValue: fallback, ...(options || {}) }) || fallback;
}

export function specSupplyPriceLabel(priceMode, currency) {
  const code = String(currency || '').trim();
  if (priceMode === 'sale') {
    return code
      ? specText('publishSpec.salePriceCode', `售价(${code}) *`, { code })
      : specText('publishSpec.salePrice', '售价 *');
  }
  if (priceMode === 'cost') {
    return code
      ? specText('publishSpec.costPriceCode', `成本价(${code}) *`, { code })
      : specText('publishSpec.costPrice', '成本价 *');
  }
  return code
    ? specText('publishSpec.priceCode', `价格(${code}) *`, { code })
    : specText('publishSpec.price', '价格 *');
}

export default function SpecSupplySection({
  colorAttr,
  sizeAttr,
  skcList = [],
  selectedSizeIds = [],
  shopId,
  categoryId = '',
  customAttrPerm = {},
  onCustomValueAdded,
  onColorsChange,
  onSizesChange,
  onUpdateSkc,
  onUpdateSku,
  onRemoveSku,
  onBatchUpdateSku,
  onSyncSkuSupply,
  printImages = [],
  onPrintImagesChange,
  currency = '',
  currencyPending = false,
  attrLoading = false,
  /** 编辑已审商品：价走专用接口，UI 提示文案 */
  isEdit = false,
  /** 'cost' | 'sale' | '' */
  priceMode = '',
  priceEditable = true,
  priceLockedHint = '',
  /** 批量商家 SKU：SPU 默认值（如货号） */
  defaultSupplierSkuSpu = '',
  printInfringement = null,
  printInfringementBusy = false,
  onAssessPrintInfringement,
  infringementCreditHint = '',
  /**
   * 官方产品模板：无店铺，上传只落 IPE，跳过 TEMU 格式转换。
   * 印花图在组件内始终 skipTemuConvert（只落 OSS，不调平台）。
   */
  skipTemuConvert = false,
  hidePrintImages = false,
  /** Temu 素材图（bg.goods.add materialImgUrl） */
  materialImage = null,
  materialImgError = '',
  onMaterialImagesChange,
  /** Temu 主图视频 productCarouseVideoReqList */
  temuVideos = [],
  videoError = '',
  onVideosChange,
  /** 素材图下方插槽（模特 / 官方尺寸信息） */
  afterMaterialContent = null,
}) {
  const { t } = useTranslation('workbench');
  const videoInputRef = useRef(null);
  const videoUploadAnchorRef = useRef(null);
  const imageSpaceRowsRef = useRef([]);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoMenuOpen, setVideoMenuOpen] = useState(false);
  const [videoLibraryOpen, setVideoLibraryOpen] = useState(false);
  const [videoLibrarySelection, setVideoLibrarySelection] = useState([]);
  const [videoPreviewTarget, setVideoPreviewTarget] = useState(null);
  const colorOptions = colorAttr?.values || [];
  const sizeOptions = sizeAttr?.values || [];
  const colorLabel = attributeDisplayLabel(colorAttr) || t('publishSpec.mainSpec');
  const sizeLabel = attributeDisplayLabel(sizeAttr) || t('publishSpec.otherSpec');
  const priceLabel = specSupplyPriceLabel(priceMode, currency);
  const priceBatchTitle = priceMode === 'sale'
    ? t('publishSpec.batchSale')
    : priceMode === 'cost'
      ? t('publishSpec.batchCost')
      : t('publishSpec.batchPrice');
  const selectedColorKeys = skcList.map(skcColorKey).filter(Boolean);
  const colorLabelByKey = useMemo(() => {
    const map = {};
    (skcList || []).forEach((skc) => {
      const key = skcColorKey(skc);
      const name = String(skc?.colorLabel || skc?.colorExtraValue || '').trim();
      if (key && name) map[key] = name;
    });
    return map;
  }, [skcList]);
  const platformSizeIds = (selectedSizeIds || []).map(String).filter(Boolean);
  const sizeLabelByKey = useMemo(() => {
    const map = {};
    (skcList || []).forEach((skc) => {
      (skc.skuRows || []).forEach((sku) => {
        if (sku.sizeIsCustom || sku.sizeExtraValue) {
          const key = makeCustomSpecKey(sku.sizeExtraValue || sku.sizeLabel || '');
          const name = String(sku.sizeLabel || sku.sizeExtraValue || '').trim();
          if (key && name) map[key] = name;
        } else if (sku.sizeValueId && sku.sizeLabel) {
          map[String(sku.sizeValueId)] = sku.sizeLabel;
        }
      });
    });
    return map;
  }, [skcList]);
  const coloredSkcs = skcList.filter(skcHasColor);
  const hasColors = coloredSkcs.length > 0;
  const [batchField, setBatchField] = useState(null);
  const [printRiskModalOpen, setPrintRiskModalOpen] = useState(false);
  const printRiskMeta = resolveTemuPrintRiskMeta(printInfringement);

  const rememberImageSpaceRows = (rows) => {
    imageSpaceRowsRef.current = appendUniqueImageSpaceRows(imageSpaceRowsRef.current, rows);
  };

  const uploadVideoFile = async (file, options = {}) => {
    if (!file || !shopId) return;
    if (file.size > MAX_PRODUCT_VIDEO_BYTES) {
      showToast(specText('publishSpec.videoTooBig20', '视频需 ≤20MB'), 'warning');
      return;
    }
    const mime = String(file.type || '').toLowerCase();
    const name = String(file.name || '').toLowerCase();
    const looksVideo = mime.startsWith('video/')
      || /\.(mp4|mov|m4v|webm|avi)$/i.test(name);
    if (!looksVideo) {
      showToast(specText('publishSpec.videoPick', '主图视频请选择视频文件（MP4/MOV 等）'), 'warning');
      return;
    }
    setVideoBusy(true);
    try {
      await assertTemuPublishVideoRatio(file);
      const res = await temuPlatformAPI.uploadProductVideo(shopId, file, {
        fromVideoSpace: options.fromVideoSpace === true,
      });
      if (!res?.vid) {
        throw new Error(specText('publishSpec.videoNoVid', '视频上传未返回平台 vid，请稍后重试或重新选择视频'));
      }
      if (!res?.videoUrl) {
        throw new Error(specText('publishSpec.videoNoUrl', '视频上传未返回可用地址，请稍后重试'));
      }
      onVideosChange?.([{
        vid: res.vid,
        videoUrl: res.videoUrl,
        coverUrl: res.coverUrl,
        width: res.width,
        height: res.height,
        // 视频空间选用时后端 ipeUrl 为空，保留成片地址便于草稿回填后预览
        ipeUrl: res.ipeUrl || options.sourceIpeUrl || '',
      }]);
      showToast(specText('publishSpec.videoOk', '视频上传成功'), 'success');
    } catch (err) {
      showToast(err?.message || specText('publishSpec.videoFail', '视频上传失败'), 'error');
    } finally {
      setVideoBusy(false);
    }
  };

  const handleVideoLibraryConfirm = async (ids) => {
    setVideoLibraryOpen(false);
    setVideoLibrarySelection([]);
    const id = Array.isArray(ids) ? ids[0] : ids;
    if (!id) return;
    const row = imageSpaceRowsRef.current.find((item) => String(item?.id) === String(id));
    if (!row) {
      showToast(specText('publishSpec.videoMissing', '未找到所选视频空间文件'), 'error');
      return;
    }
    try {
      const sourceIpeUrl = resolveVideoSpacePlayUrl(row)
        || String(row?.outputVideoUrl || row?._playUrl || '').trim();
      const file = await videoSpaceRecordToUploadFile(row, 'video.mp4');
      await uploadVideoFile(file, { fromVideoSpace: true, sourceIpeUrl });
    } catch (err) {
      showToast(err?.message || specText('publishSpec.videoSpaceFail', '视频空间读取失败'), 'error');
    }
  };

  const supplierSkuColorOptions = useMemo(() => (
    (skcList || [])
      .filter(skcHasColor)
      .map((skc) => ({
        key: skcColorKey(skc),
        label: attributeValueDisplayLabel(skc.colorLabel || skc.colorExtraValue) || skc.colorLabel || specText('publishSpec.unnamedColor', '未命名颜色'),
      }))
      .filter((row) => row.key)
  ), [skcList]);

  const applyBatch = (raw) => {
    const text = String(raw ?? '').trim();
    if (!batchField) return;
    if (batchField === 'supplierSku' || batchField === 'supplierCode') return;
    if (!text) {
      setBatchField(null);
      return;
    }
    onBatchUpdateSku?.(batchField === 'suggestedPrice'
      ? {
        suggestedPrice: text,
        suggestedPriceCurrencyType: currency || 'USD',
      }
      : { [batchField]: text });
    setBatchField(null);
  };

  const applySupplierCodeBatch = (payload) => {
    const {
      spu,
      patternName,
      colorCodes,
      includePatternInSku,
    } = payload || {};
    if (typeof onUpdateSkc === 'function' && colorCodes) {
      (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
        if (!skcHasColor(skc)) return;
        const colorKey = skcColorKey(skc);
        const colorCode = sanitizeSkuPart(colorCodes?.[colorKey]);
        if (!colorCode) return;
        onUpdateSkc(skc.id, {
          colorCode,
          supplierCode: buildSkcSupplierCode({
            spu,
            colorCode,
            patternName,
          }),
        });
      });
    }
    onBatchUpdateSku?.((skc, sku) => {
      const colorKey = skcColorKey(skc);
      const colorCode = sanitizeSkuPart(colorCodes?.[colorKey]);
      const sizeName = resolveSkuSizeLabel(sku, sizeOptions);
      if (!colorCode || !sizeName) return null;
      return {
        supplierSku: buildMerchantSupplierSku({
          spu,
          colorCode,
          sizeLabel: sizeName,
          patternName,
          includePattern: Boolean(includePatternInSku),
        }),
      };
    });
    setBatchField(null);
  };

  return (
    <div className="tpo-spec-supply">
      {/* —— 主规格：销售属性 + 商品图 —— */}
      <div className="tpo-spec-block">
        {/* 印花图：主规格标题上方（对齐 SHEIN） */}
        {!hidePrintImages ? (
          <div className="tpo-spec-print">
            <div className="tpo-spec-print__head">
              <span className="tpo-field__label tpo-th-with-help">
                {t('publishSpec.print')}
                <FieldHelpMark text={t('publishSpec.printHelpTemu')} />
              </span>
              <div className="tpo-spec-print__actions">
                <button
                  type="button"
                  className="tpo-btn tpo-btn--danger tpo-btn--sm tpo-spec-print__risk-btn"
                  disabled={printInfringementBusy || printRiskMeta.meta.assessing}
                  title={infringementCreditHint || undefined}
                  onClick={() => { void onAssessPrintInfringement?.(); }}
                >
                  {printRiskMeta.meta.assessing ? t('publishSpec.assessing') : t('publishSpec.infringement')}
                </button>
                <button
                  type="button"
                  className={`tpo-infr-risk-btn${printRiskMeta.canOpenRisk ? ' is-clickable' : ''}`}
                  disabled={!printRiskMeta.canOpenRisk}
                  onClick={() => {
                    if (!printRiskMeta.canOpenRisk) return;
                    setPrintRiskModalOpen(true);
                  }}
                >
                  <span className={`tpo-infr-risk-badge ${temuPrintRiskBadgeClass(printRiskMeta.meta.risk, printRiskMeta.meta.assessing)}`}>
                    {printRiskMeta.riskLabel}
                  </span>
                  {printRiskMeta.canOpenRisk ? (
                    <span className="tpo-infr-risk-hint">{t('publishSpec.viewRisk')}</span>
                  ) : null}
                </button>
              </div>
            </div>
            <PublishImageUploadZone
              compact
              labelInside
              label={t('publishSpec.print')}
              imageType={2}
              multiple
              maxCount={MAX_PRINT_IMAGES}
              uploadSlotFirst
              prependNew
              reorderable
              enforceTemuRules={false}
              skipTemuConvert
              value={Array.isArray(printImages) ? printImages : []}
              shopId={shopId}
              onChange={(imgs) => onPrintImagesChange?.(
                Array.isArray(imgs) ? imgs.slice(0, MAX_PRINT_IMAGES) : [],
              )}
            />
          </div>
        ) : null}

        <h4 className="tpo-publish__subhead">{t('publishSpec.mainSpec')}</h4>

        <div className="tpo-spec-row">
          <div className="tpo-spec-attr">
            <span className="tpo-field__label">{colorLabel} <RequiredMark /></span>
            <SpecValuePicker
              options={colorOptions}
              value={selectedColorKeys}
              onChange={onColorsChange}
              labelByKey={colorLabelByKey}
              placeholder={attrLoading ? t('returnList.loading') : t('publishSpec.pickCustom', { label: colorLabel })}
              disabled={attrLoading || !colorAttr}
              emptyText={t('publishSpec.emptyEnter')}
              disabledHint={attrLoading ? t('returnList.loading') : t('publishSpec.loadAttr', { label: colorLabel })}
              allowCustom
              addMode
              allowPlatformCustom={!!customAttrPerm[String(colorAttr?.attributeId || '')]}
              shopId={shopId}
              categoryId={categoryId}
              attributeId={colorAttr?.attributeId || ''}
              attributeLabel={colorLabel}
              onPlatformCustomAdded={(opt) => onCustomValueAdded?.(colorAttr?.attributeId, opt)}
            />
          </div>

          <div className="tpo-spec-attr">
            <span className="tpo-field__label">{sizeLabel}{sizeAttr?.required ? <RequiredMark /> : null}</span>
            <SpecValuePicker
              options={sizeOptions}
              value={platformSizeIds}
              onChange={onSizesChange}
              labelByKey={sizeLabelByKey}
              placeholder={attrLoading ? t('returnList.loading') : t('publishSpec.pickLabel', { label: sizeLabel })}
              disabled={attrLoading || !hasColors || !sizeAttr}
              emptyText={t('publishSpec.emptySize')}
              disabledHint={!hasColors ? t('publishSpec.pickFirst', { label: colorLabel }) : (!sizeAttr ? t('publishSpec.noAttr', { label: sizeLabel }) : t('publishSpec.unavailable'))}
              shopId={shopId}
              categoryId={categoryId}
              attributeId={sizeAttr?.attributeId || ''}
              attributeLabel={sizeLabel}
            />
          </div>
        </div>

        {hasColors ? (
          <div className="tpo-publish__table-wrap tpo-skc-img-wrap">
            <table className="tpo-publish__table tpo-skc-img-table">
              <thead>
                <tr>
                  <th className="tpo-skc-img-table__color-head">{colorLabel}</th>
                  <th>
                    <span className="tpo-th-with-help">
                      {t('publishSpec.productImages')}
                      <FieldHelpMark text={specText('publishSpec.productImagesHintTemu', TEMU_SKC_PRODUCT_IMAGE_HINT)} />
                    </span>
                  </th>
                  <th className="tpo-skc-img-table__ext-code-head">
                    <span className="tpo-supply-th">
                      <span>{t('tiktokCatalog.supplier')} <RequiredMark /></span>
                      <button
                        type="button"
                        className="tpo-supply-th__batch"
                        onClick={() => setBatchField('supplierCode')}
                      >
                        {t('publishSpec.batch')}
                      </button>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {coloredSkcs.map((skc) => (
                  <tr key={skc.id}>
                    <td className="tpo-skc-img-table__color">
                      <strong>{skc.colorLabel || '—'}</strong>
                      {skc.colorIsCustom ? (
                        <div className="tpo-skc-img-table__skc-id">{t('publishSpec.custom')}</div>
                      ) : null}
                      {skc.skcCode ? (
                        <div className="tpo-skc-img-table__skc-id">{skc.skcCode}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="tpo-skc-img-table__details">
                        <PublishImageUploadZone
                          compact
                          labelInside
                          label={t('publishSpec.mainStar')}
                          imageType={1}
                          value={skc.mainImage ? [skc.mainImage] : []}
                          shopId={shopId}
                          skipTemuConvert={skipTemuConvert}
                          enforceTemuRules
                          onChange={async (img) => {
                            if (!img) {
                              onUpdateSkc(skc.id, {
                                mainImage: null,
                                squareImage: null,
                                colorBlockImage: null,
                              });
                              // 仅第一色主图驱动素材图：清空第一色且素材图为自动裁时一并清空
                              const isFirstColor = coloredSkcs[0]?.id === skc.id;
                              if (
                                isFirstColor
                                && typeof onMaterialImagesChange === 'function'
                                && materialImage?._fromMainCrop
                              ) {
                                onMaterialImagesChange(null);
                              }
                              return;
                            }
                            onUpdateSkc(skc.id, {
                              mainImage: img,
                              // Temu 无需方块图；色块图由主图自动裁切上传（对齐 Shein）
                              squareImage: null,
                            });
                            try {
                              const colorBlock = await deriveTemuColorBlockFromMainSlot({
                                shopId,
                                mainSlot: img,
                                skipTemuConvert,
                              });
                              if (colorBlock) {
                                onUpdateSkc(skc.id, { colorBlockImage: colorBlock });
                              }
                            } catch (e) {
                              showToast(
                                e?.message || specText('publishSpec.colorCropFail', '色块图自动生成失败，发布前将重试'),
                                'warning',
                              );
                            }
                            // 仅用第一个颜色的主图自动裁 1:1 素材图（手传独立素材图不覆盖）
                            const isFirstColor = coloredSkcs[0]?.id === skc.id;
                            if (isFirstColor && typeof onMaterialImagesChange === 'function') {
                              const materialTemu = String(materialImage?.temuUrl || '').trim();
                              const mainTemu = String(img?.temuUrl || '').trim();
                              const keepManual = Boolean(
                                materialTemu
                                && materialTemu !== mainTemu
                                && !materialImage?._fromMainCrop,
                              );
                              if (!keepManual) {
                                try {
                                  const material = await deriveTemuMaterialFromMainSlot({
                                    shopId,
                                    mainSlot: img,
                                    skipTemuConvert,
                                  });
                                  if (material) onMaterialImagesChange(material);
                                } catch (e) {
                                  showToast(
                                    e?.message || specText('publishSpec.materialCropFail', '素材图自动生成失败，发布前将重试'),
                                    'warning',
                                  );
                                }
                              }
                            }
                          }}
                        />
                        <PublishImageUploadZone
                          compact
                          labelInside
                          label={t('publishSpec.detail')}
                          imageType={2}
                          multiple
                          maxCount={MAX_DETAIL_IMAGES}
                          uploadSlotFirst
                          prependNew
                          reorderable
                          value={Array.isArray(skc.detailImages) ? skc.detailImages : []}
                          shopId={shopId}
                          skipTemuConvert={skipTemuConvert}
                          enforceTemuRules
                          onChange={(imgs) => onUpdateSkc(skc.id, {
                            detailImages: Array.isArray(imgs) ? imgs.slice(0, MAX_DETAIL_IMAGES) : [],
                          })}
                        />
                      </div>
                    </td>
                    <td className="tpo-skc-img-table__ext-code">
                      <input
                        className="tpo-input"
                        value={skc.supplierCode || ''}
                        placeholder={specText('publishSpec.likeSupplierColor', '如 SPU-颜色编码')}
                        onChange={(e) => onUpdateSkc(skc.id, { supplierCode: e.target.value })}
                        aria-label={`${skc.colorLabel || colorLabel}${t('tiktokCatalog.supplier')}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {typeof onMaterialImagesChange === 'function' ? (
        <div className="tpo-media-stack" data-field-error="materialImgUrl">
          <div className={`tpo-field tpo-media-stack__item${materialImgError ? ' tpo-field--invalid' : ''}`}>
            <span className="tpo-field__label tpo-field__label-with-help">
              {t('publishSpec.material')} <RequiredMark />
              <FieldHelpMark text={specText('publishSpec.materialHintTemu', TEMU_MATERIAL_HINT)} />
            </span>
            <PublishImageUploadZone
              compact
              labelInside
              label={t('publishSpec.material')}
              imageType={1}
              imageRule="material"
              multiple={false}
              maxCount={1}
              enforceTemuRules
              skipTemuConvert={skipTemuConvert}
              value={materialImage ? [materialImage] : []}
              shopId={shopId}
              onChange={onMaterialImagesChange}
            />
            {materialImgError ? (
              <p className="tpo-wizard__inline-error" role="alert">{materialImgError}</p>
            ) : null}
          </div>

          {typeof onVideosChange === 'function' ? (
            <div className={`tpo-field tpo-media-stack__item${videoError ? ' tpo-field--invalid' : ''}`} data-field-error="temuVideos">
              <span className="tpo-field__label tpo-field__label-with-help">
                {t('publishSpec.mainVideo')}
                <FieldHelpMark text={specText('publishSpec.mainVideoHintTemu', TEMU_MAIN_VIDEO_HINT)} />
              </span>
              <div className="tpo-video-upload tpo-video-upload--compact">
                {(Array.isArray(temuVideos) ? temuVideos : []).slice(0, 1).map((v) => {
                  const playSrc = String(v.videoUrl || v.ipeUrl || '').trim();
                  const posterSrc = String(v.coverUrl || '').trim();
                  return (
                    <div key={v.vid || playSrc || v.ipeUrl} className="tpo-video-upload__thumb">
                      {posterSrc ? (
                        <img src={posterSrc} alt="" />
                      ) : playSrc ? (
                        <video src={playSrc} muted preload="metadata" playsInline />
                      ) : (
                        <span className="tpo-video-upload__placeholder">{specText('publishSpec.video', fixedUi("视频"))}</span>
                      )}
                      <button
                        type="button"
                        className="tpo-img-upload__remove"
                        aria-label={specText('publishSpec.removeVideo', '移除视频')}
                        disabled={videoBusy}
                        onClick={() => onVideosChange([])}
                      >
                        ×
                      </button>
                      {playSrc ? (
                        <button
                          type="button"
                          className="tpo-img-upload__preview"
                          onClick={() => setVideoPreviewTarget({
                            name: t('publishSpec.mainVideo'),
                            videoUrl: playSrc,
                            outputVideoUrl: playSrc,
                            coverUrl: posterSrc,
                            posterUrl: posterSrc,
                            outputVideoPosterUrl: posterSrc,
                            previewUrl: posterSrc,
                            width: v.width,
                            height: v.height,
                          })}
                        >
                          {specText('publishSpec.preview', '预览')}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
                {!(temuVideos || []).length ? (
                  <button
                    ref={videoUploadAnchorRef}
                    type="button"
                    className={`tpo-video-upload__drop${videoBusy ? ' tpo-img-upload__drop--busy' : ''}`}
                    disabled={videoBusy || !shopId}
                    onClick={() => setVideoMenuOpen(true)}
                  >
                    {videoBusy ? (
                      <span className="tpo-img-upload__busy" aria-live="polite">
                        <span className="tpo-img-upload__spinner" aria-hidden />
                        <span className="tpo-img-upload__busy-text">{specText('publishSpec.uploading', fixedUi("上传中"))}</span>
                      </span>
                    ) : (
                      <>
                        <span className="tpo-img-upload__slot-label">{t('publishSpec.mainVideo')}</span>
                        <span className="tpo-img-upload__slot-action">{specText('publishSpec.clickUpload', fixedUi("点击上传"))}</span>
                      </>
                    )}
                  </button>
                ) : null}
                <input
                  ref={videoInputRef}
                  type="file"
                  accept={TEMU_PUBLISH_VIDEO_ACCEPT}
                  hidden
                  disabled={videoBusy}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) await uploadVideoFile(file);
                  }}
                />
                <TemuUploadSourceMenu
                  open={videoMenuOpen}
                  anchorRef={videoUploadAnchorRef}
                  onClose={() => setVideoMenuOpen(false)}
                  disabled={videoBusy}
                  localLabel={specText('publishSpec.localFile', '本地文件')}
                  imageSpaceLabel={specText('publishSpec.videoSpace', '视频空间')}
                  onPickLocal={() => videoInputRef.current?.click()}
                  onPickImageSpace={() => setVideoLibraryOpen(true)}
                />
                <LibraryModalContent
                  isOpen={videoLibraryOpen}
                  mediaKind="video"
                  onClose={() => {
                    setVideoLibraryOpen(false);
                    setVideoLibrarySelection([]);
                  }}
                  images={[]}
                  selection={videoLibrarySelection}
                  onToggle={(id) => {
                    setVideoLibrarySelection((prev) => (
                      prev.includes(id) ? [] : [id]
                    ));
                  }}
                  maxSelection={1}
                  onConfirm={handleVideoLibraryConfirm}
                  onImagesAppended={rememberImageSpaceRows}
                  uploadOriginSource="upload"
                  showToast={showToast}
                  deferUsageBump
                  selectAllLabel={specText('publishSpec.selectLoaded', '全选已加载')}
                  closeOnBackdropClick={false}
                />
                <VideoSpacePreviewModal
                  open={Boolean(videoPreviewTarget)}
                  video={videoPreviewTarget}
                  navList={videoPreviewTarget ? [videoPreviewTarget] : []}
                  onClose={() => setVideoPreviewTarget(null)}
                />
              </div>
              {videoError ? (
                <p className="tpo-wizard__inline-error" role="alert">{videoError}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {afterMaterialContent}

      {/* —— 供应信息 —— */}
      {hasColors ? (
        <div className="tpo-spec-block">
          <h4 className="tpo-publish__subhead">{t('publishSpec.supply')}</h4>
          {isEdit ? (
            <p className="tpo-wizard__inline-hint">
              {priceMode === 'sale'
                ? ''
                : priceMode === 'cost'
                  ? t('publishSpec.costHint')
                  : t('publishSpec.priceHint')}
              {priceLockedHint ? ` ${priceLockedHint}` : ''}
            </p>
          ) : null}

          <div className="tpo-publish__table-wrap">
            <table className="tpo-publish__table tpo-supply-table">
              <thead>
                <tr>
                  <th className="tpo-supply-table__color">{colorLabel}</th>
                  <th className="tpo-supply-table__size">{sizeLabel}</th>
                  {priceEditable ? (
                    <BatchHeader onBatch={() => setBatchField('price')}>
                      {withRequiredMark(priceLabel)}
                    </BatchHeader>
                  ) : (
                    <th>{withRequiredMark(priceLabel)}</th>
                  )}
                  <BatchHeader onBatch={() => setBatchField('suggestedPrice')}>
                    {t('publishSpec.msrp')}
                  </BatchHeader>
                  <BatchHeader onBatch={() => setBatchField('stock')}>{t('productTable.stock')} <RequiredMark /></BatchHeader>
                  <BatchHeader onBatch={() => setBatchField('supplierSku')}>{t('publishSpec.merchantSku')}</BatchHeader>
                </tr>
              </thead>
              <tbody>
                {coloredSkcs.flatMap((skc) => {
                  const rows = skc.skuRows?.length ? skc.skuRows : [{ id: `${skc.id}-empty` }];
                  return rows.map((sku, rowIndex) => {
                    const sizeName = sku.sizeIsCustom || sku.sizeExtraValue
                      ? (attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '—')
                      : (
                        attributeValueDisplayLabel(
                          sizeOptions.find((v) => String(v.id) === String(sku.sizeValueId))
                          || sku.sizeLabel,
                        ) || '—'
                      );
                    return (
                      <tr key={`${skc.id}-${sku.id || rowIndex}`}>
                        {rowIndex === 0 ? (
                          <td rowSpan={rows.length} className="tpo-supply-table__color">
                            <strong>{skc.colorLabel || '—'}</strong>
                            {skc.skcCode ? (
                              <div className="tpo-skc-img-table__skc-id">{skc.skcCode}</div>
                            ) : null}
                          </td>
                        ) : null}
                        <td className="tpo-supply-table__size">{sizeName}</td>
                        <td>
                          <div className="tpo-price-cell">
                            <input
                              className="tpo-input"
                              value={sku.price ?? ''}
                              disabled={!priceEditable}
                              title={!priceEditable ? (priceLockedHint || t('publishSpec.priceLockedTitle')) : undefined}
                              onChange={(e) => onUpdateSku(skc.id, sku.id, { price: e.target.value })}
                              placeholder="0.00"
                            />
                            {currency ? <span className="tpo-price-cell__unit">{currency}</span> : null}
                          </div>
                        </td>
                        <td>
                          <div className="tpo-price-cell">
                            <input
                              className="tpo-input"
                              value={sku.suggestedPrice ?? ''}
                              onChange={(e) => onUpdateSku(skc.id, sku.id, {
                                suggestedPrice: e.target.value,
                                suggestedPriceCurrencyType: currency || sku.suggestedPriceCurrencyType || 'USD',
                              })}
                              placeholder={specText('publishPkg.optionalPh', '选填')}
                              inputMode="decimal"
                            />
                            <span className="tpo-price-cell__unit">{currency || 'USD'}</span>
                          </div>
                        </td>
                        <td>
                          <input
                            className="tpo-input"
                            value={sku.stock ?? ''}
                            onChange={(e) => onUpdateSku(skc.id, sku.id, { stock: e.target.value })}
                            placeholder={t('publishSpec.fill')}
                          />
                        </td>
                        <td>
                          <input
                            className="tpo-input"
                            value={sku.supplierSku ?? ''}
                            onChange={(e) => onUpdateSku(skc.id, sku.id, { supplierSku: e.target.value })}
                            placeholder={specText('publishSpec.likeColorSize', '如 SPU-颜色-尺码')}
                          />
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>

          {batchField === 'supplierSku' || batchField === 'supplierCode' ? (
            <SupplierCodeBatchModal
              title={batchField === 'supplierCode' ? specText('publishSpec.batchSupplierOnly', '批量填写货号') : specText('publishSpec.batchMerchantSku', '批量填写商家 SKU')}
              colors={supplierSkuColorOptions}
              defaultSpu={defaultSupplierSkuSpu}
              skcList={skcList}
              onClose={() => setBatchField(null)}
              onConfirm={applySupplierCodeBatch}
            />
          ) : batchField ? (
            <BatchFillModal
              field={batchField}
              currency={currency}
              titleOverride={batchField === 'price' ? priceBatchTitle : undefined}
              onClose={() => setBatchField(null)}
              onConfirm={applyBatch}
            />
          ) : null}
        </div>
      ) : null}

      <PrintInfringementResultModal
        open={printRiskModalOpen}
        riskLabel={printRiskMeta.riskLabel}
        assessedAt={printRiskMeta.meta.assessedAt}
        body={printRiskMeta.meta.riskAdvice}
        onClose={() => setPrintRiskModalOpen(false)}
      />
    </div>
  );
}
